import React, { useMemo, useCallback } from 'react';
import { __ } from '@wordpress/i18n';
import { useBookingStore } from '../store/useBookingStore';
import { useService, useEffectiveDuration } from '../hooks/useBookingComputed';
import { useAvailableSlotsQuery } from '../queries';
import {
	formatFullDate,
	formatTimeRange,
	formatCalendarDate,
} from '../utils/date-time';
import './SimpleSlotPicker.css';

interface SimpleSlot {
	date: string; // YYYY-MM-DD
	time: string | null; // HH:mm or null for full-day
	remainingSlots: number;
}

export function SimpleSlotPicker() {
	const { data: service } = useService();

	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const slotMode = useBookingStore((state) => state.slotMode);
	const interval = useBookingStore((state) => state.interval);
	const simpleLimit = useBookingStore((state) => state.simpleLimit);
	const selectedResourceIds = useBookingStore(
		(state) => state.selectedResourceIds
	);
	const resourceMode = useBookingStore((state) => state.resourceMode);
	const showEndTimes = useBookingStore((state) => state.showEndTimes);
	const showSpotsLeft = useBookingStore((state) => state.showSpotsLeft);
	const spotsLeftText = useBookingStore((state) => state.spotsLeftText);

	const selectedRange = useBookingStore((state) => state.selectedRange);
	const setSelectedDate = useBookingStore((state) => state.setSelectedDate);
	const setSelectedTime = useBookingStore((state) => state.setSelectedTime);
	const setSelectedDuration = useBookingStore(
		(state) => state.setSelectedDuration
	);

	const effectiveDuration = useEffectiveDuration();

	const {
		data: slotsData,
		isLoading,
		error,
	} = useAvailableSlotsQuery(
		{
			serviceId: currentServiceId,
			resources: selectedResourceIds,
			limit: simpleLimit,
			slotMode: slotMode === 'flexible' ? 'flexible' : 'fixed',
			interval,
			duration: effectiveDuration,
			resourceMode,
		},
		{
			enabled: !!currentServiceId && !!service,
		}
	);

	const blockSize = slotsData?.blockSize ?? service?.blockSize ?? 0;
	const isFullDay = blockSize >= 24 * 60;

	const selectedKey = useMemo(() => {
		if (!selectedRange?.from) {
			return null;
		}
		const date = formatCalendarDate(selectedRange.from);
		if (isFullDay) {
			return date;
		}
		const hours = String(selectedRange.from.getHours()).padStart(2, '0');
		const mins = String(selectedRange.from.getMinutes()).padStart(2, '0');
		return `${date}_${hours}:${mins}`;
	}, [selectedRange, isFullDay]);

	const handleSlotSelect = useCallback(
		(slot: SimpleSlot) => {
			// Parse the date string into a local Date object
			const [year, month, day] = slot.date.split('-').map(Number);
			const date = new Date(year, month - 1, day);

			setSelectedDate(date);

			if (slot.time) {
				// Time-slot service: set the time which also computes end
				setSelectedTime(slot.time);
			} else {
				// Full-day service: set duration based on block size
				setSelectedDuration(blockSize);
			}
		},
		[setSelectedDate, setSelectedTime, setSelectedDuration, blockSize]
	);

	const formatSpotsLeftText = useCallback(
		(remainingSlots: number): string => {
			if (spotsLeftText) {
				const pluralMatch = spotsLeftText.match(
					/\[\[([^|]+)\|([^\]]+)\]\]/
				);
				let text = spotsLeftText.replace(
					'{spots}',
					remainingSlots.toString()
				);
				if (pluralMatch) {
					const replacement =
						remainingSlots === 1 ? pluralMatch[2] : pluralMatch[1];
					text = text.replace(pluralMatch[0], replacement);
				}
				return text;
			}
			return remainingSlots === 1
				? __('1 spot left', 'gp-bookings')
				: /* translators: %d: number of spots remaining */
					__('%d spots left', 'gp-bookings').replace(
						'%d',
						remainingSlots.toString()
					);
		},
		[spotsLeftText]
	);

	const formatSlotLabel = useCallback(
		(slot: SimpleSlot): string => {
			const [year, month, day] = slot.date.split('-').map(Number);
			const date = new Date(year, month - 1, day);
			const dateStr = formatFullDate(date);

			if (isFullDay || !slot.time) {
				return dateStr;
			}

			const timeStr = formatTimeRange(slot.time, blockSize, showEndTimes);
			return `${dateStr} — ${timeStr}`;
		},
		[blockSize, showEndTimes, isFullDay]
	);

	if (isLoading) {
		return (
			<div className="gpb-simple-slot-picker gpb-simple-slot-picker--loading gform-theme__no-reset--el">
				<span className="gform-loader" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="gpb-simple-slot-picker gpb-simple-slot-picker--error">
				<p>
					{__('Unable to load available time slots.', 'gp-bookings')}
				</p>
			</div>
		);
	}

	const slots = slotsData?.slots ?? [];

	if (slots.length === 0) {
		return (
			<div className="gpb-simple-slot-picker gpb-simple-slot-picker--empty">
				<p>{__('No available time slots found.', 'gp-bookings')}</p>
			</div>
		);
	}

	return (
		<div className="gpb-simple-slot-picker gform-field-label--type-inline gfield--type-choice">
			<div
				className="gfield_radio"
				role="radiogroup"
				aria-label={__('Available time slots', 'gp-bookings')}
			>
				{slots.map((slot, index) => {
					const key = slot.time
						? `${slot.date}_${slot.time}`
						: slot.date;
					const isSelected = selectedKey === key;
					const label = formatSlotLabel(slot);

					return (
						<div key={key} className="gchoice">
							<input
								className="gfield-choice-input"
								type="radio"
								name="gpb-simple-slot"
								value={key}
								id={`gpb-simple-slot-${index}`}
								checked={isSelected}
								onChange={() => handleSlotSelect(slot)}
							/>
							<label
								htmlFor={`gpb-simple-slot-${index}`}
								className="gform-field-label gform-field-label--type-inline"
							>
								{label}
								{showSpotsLeft && slot.remainingSlots > 0 && (
									<span className="gpb-simple-slot-picker__spots">
										{' — '}
										{formatSpotsLeftText(
											slot.remainingSlots
										)}
									</span>
								)}
							</label>
						</div>
					);
				})}
			</div>
		</div>
	);
}
